import * as Enumerable from 'linq';
import { DialogModule } from 'primeng/dialog';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, effect, EventEmitter, inject, Input, Output, ViewEncapsulation, WritableSignal } from '@angular/core';
import { Activity, ActivityEntityName, ActivityKind, ActivityShareType, DialogType, EmailContext, FormPickerRequest, Job, ShareableEntity } from '@jms/model';
import { StateComponent } from '@jms/utils/util.state.component';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { SelectionService, WorkBuddyDialogService } from '@workbuddy/ui/services';
import { SignalInput } from '@workbuddy/utils/signals';
import { EntityCreatorService } from '@workbuddy/modules/creators';

import { ActivityContext, ActivityDisplayMode, ActivityFilter, ActivityFilterType, OVERRIDE_ACTIVITY_SERVICE } from '../../model';
import { ActivityService } from '../../services';
import { UiActivityUtility } from '../../utilities';
import { ActivityHeaderComponent } from '../activity.header';
import { ActivityListComponent } from '../activity.list';
import { ShellDialogService } from '@jms/ui/ui.shell.dialog.service';
import { interval, startWith, take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';



interface ActivityDashboardComponentVM {
  context: ActivityContext;
  activities: Activity[];
  filteredActivities: Activity[];
  display: ActivityDisplayMode;
  selected: Activity;
  filter: ActivityFilter;
  dialog: DialogType;
  job: Job;
  timezone: string;
  emailContext: EmailContext;
}

const initialState = {
  context: null,
  activities: [],
  filteredActivities: [],
  display: ActivityDisplayMode.SideBar,
  selected: null,
  filter: null,
  dialog: DialogType.None,
  job: null,
  timezone: null,
  emailContext: null,
}
@Component({
  selector: 'activity-dashboard',
  templateUrl: './activity.dashboard.component.html',
  styleUrls: ['./activity.dashboard.component.less'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    CommonModule,

    DialogModule,

    ActivityListComponent,
    ActivityHeaderComponent,
  ],
  providers: [
    ActivityService,
    SelectionService,
    WorkBuddyDialogService,
    EntityCreatorService,
    {
      provide: ConfirmServicePro,
      useClass: ConfirmServicePro,
    },
    {
      provide: ShellDialogService,
      useClass: ShellDialogService,
    }
  ]
})
export class ActivityDashboardComponent extends StateComponent<ActivityDashboardComponentVM> {

  @SignalInput() @Input('context') public $context: WritableSignal<ActivityContext>;

  @Output() public dialogVisible = new EventEmitter<boolean>();
  @Output() public activityCreated = new EventEmitter<Activity>();
  @Output() public activitySaved = new EventEmitter<Activity>();
  @Output() public link = new EventEmitter<string[]>();

  private readonly INTERVAL_DURATION_IN_MS = 2000;
  private readonly MAX_REFRESH_ATTEMPTS = 10;

  protected timezone = null;

  private _selectionService = inject(SelectionService);
  private _activities = inject(ActivityService);
  private _confirmService = inject(ConfirmServicePro);
  private _destroyRef = inject(DestroyRef);
  private creatorService = inject(EntityCreatorService);
  // Both are provided on this component, so pointing them at the host reaches every dialog raised from
  // here: the editors, and the confirms those editors open through the same scoped services.
  private readonly dialogService = inject(WorkBuddyDialogService);
  private readonly shellDialogService = inject(ShellDialogService);

  private _overrideActivityService = inject(OVERRIDE_ACTIVITY_SERVICE, { optional: true }) as ActivityService;

  protected get activityService() {
    return this._overrideActivityService ?? this._activities;
  }

  // protected get $activities() {
  //   // return this._overrideActivityService?.$activities ?? this._activities?.$activities;
  //   const abc = this.activityService.$activities;
  //   return abc;
  // }

  protected DialogType = DialogType;
  protected ActivityKind = ActivityKind;
  protected ActivityDisplayMode = ActivityDisplayMode;

  constructor() {

    super(initialState);

    effect(() => {

      const context = this.$context();
      if (!context) return;

      const { entity, job, display, timezone, customer, sendFrom } = context;

      this.dialogService.appendTo = context.dialogHost ?? null;
      this.shellDialogService.appendTo = context.dialogHost ?? null;

      const emailContext = {
        entity: entity?.type,
        entityId: entity?._id,
        sendFrom: sendFrom,
        customer: customer,
      } as EmailContext;


      this.setStateDirect({
        display: display,
        job: job,
        timezone: timezone,
        filteredActivities: [],
        emailContext: emailContext,
        context: context
      });

      this.activityService.getAll(entity?._id, context?.searchOptions);

    });

    effect(() => {
      const activities = this.activityService.$activities();
      const { context, filter } = this.vm;
      // Relation tabs can mount before the host record has an _id (new Invoice). Context is null then.
      if (!context) return;

      if (activities?.length) {
        const tags = Enumerable.from(activities)
          .selectMany(x => x.labels)
          .where(x => !!x?._id)
          .distinct(x => x._id)
          .toArray();

        context.tagsForFilter = tags;
      }

      context.currentFilter = filter;

      this.setStateDirect({ activities: activities, context: { ...context } });
      // this.onFilter(this?.vm?.filter);

      setTimeout(() => {
        this._selectionService.clear();
      });

    });

  }

  protected onEdit(activity: Activity) {

    this.activityService
      .getById(activity?._id)
      .subscribe(loaded => {
        this.creatorService.show({
          entity: ActivityEntityName.Activity,
          config: {
            data: {
              activity: loaded,
              context: this.vm.context,
              ...this.editorHandlers(),
              onSaveCompletedForm: () => this.onSaveCompletedForm(loaded),
            },
          },
        });
      });

  }

  private editorHandlers() {

    return {
      submit: (activity: Activity) => this.activityService.save(activity),
      manage: {
        share: (request: ShareableEntity) => this.activityService.share(request),
        delete: (id: string) => this.activityService.delete(id),
        updateTags: (request: Partial<Activity>) => this.activityService.updateTags(request),
      },
    };
  }

  protected onFilter(filter: ActivityFilter) {

    if (!filter || filter === undefined) {
      filter = {
        search: null,
        excludeKinds: [],
        type: ActivityFilterType.User,
        sharing: 'All',
        tags: [],
      }
    }

    this.setState({ filter: filter });

    const tags = new Set(filter.tags.map(x => x._id));

    const filtered = Enumerable
      .from(this.vm.activities)
      .where(x => {

        filter.type = this.$context()?.filterOptions?.overrideDefaultFilterType
          ? this.$context()?.filterOptions?.overrideDefaultFilterType
          : filter.type;

        switch (filter?.type) {
          case ActivityFilterType.System:
            return x.system == true;
          case ActivityFilterType.User:
            return x.system !== true;
          default:
            return true;
        }
      })
      .where(x => {
        const excludeKinds = filter?.excludeKinds ?? [];
        const excluded = excludeKinds?.includes(x?.kind);
        return !excluded;
      })
      .where(x => {

        const { sharing } = filter;

        if (!sharing) return true;

        if (sharing === ActivityShareType.NotShared)
          return x?.effectiveSharing?.shared?.length == 0;

        if (sharing === ActivityShareType.Customer)
          return x?.effectiveSharing?.shared?.includes(ActivityShareType.Customer);

        if (sharing === ActivityShareType.Field)
          return x?.effectiveSharing?.shared?.includes(ActivityShareType.Field);

        return true;
      })
      .where(x => {
        if (tags.size === 0) {
          return true;
        }

        if (!x.labels?.length) {
          return false
        }

        return x.labels?.some(x => tags.has(x._id));
      })
      .where(x => {

        if (!filter?.search?.length) {
          return true;
        }

        const search = filter.search.toLowerCase();
        const name = x.name?.toLowerCase();
        const note = x.note?.toLowerCase();
        const attachments = x.attachments?.map(x => x.title).join(', ').toLowerCase();
        const people = Enumerable
          .from(x?.from ? [x?.from] : [])
          .concat(x?.to ?? [])
          .concat(x?.cc ?? [])
          .concat(x?.bcc ?? [])
          .select(x => x?.name)
          .toArray()
          .join(', ')
          .toLowerCase();

        return name?.includes(search) ||
          note?.includes(search) ||
          attachments?.includes(search) ||
          people?.includes(search);

      })
      .toArray();


    this.setState({ filteredActivities: filtered, filter: filter });
  }

  /**
   * Put mergeWiths, jobType into the form picker request
   * @param context Activity context of this dashboard
   * @returns The form picker request for the form activity
   */
  private getFormPickerRequest(context: ActivityContext): FormPickerRequest {
    let jobTypeId;
    let assetTypeId;

    if (context.entity?.type === ActivityEntityName.Opportunity) {
      jobTypeId = context.opportunity?.jobType?._id;
    } else if (context.entity?.type === ActivityEntityName.Asset) {
      assetTypeId = context.asset?.assetType?._id;
    }
    else {
      jobTypeId = context.job?.type?._id;
    }

    const formPickerRequest = context.formPickerRequest;

    return {
      mergeWiths: [context.entity?.type],
      jobType: jobTypeId ?? formPickerRequest?.jobType,
      assetType: assetTypeId ?? formPickerRequest?.assetType,
    };
  }

  protected async onAddActivityByKind(kind: ActivityKind) {

    const context = kind === ActivityKind.Form
      ? { ...this.vm.context, formPickerRequest: this.getFormPickerRequest(this.vm.context) }
      : { ...this.vm.context };

    const activity = UiActivityUtility.buildActivityForKind(kind, context);

    this.creatorService.show({
      entity: ActivityEntityName.Activity,
      config: {
        data: {
          activity,
          context,
          submit: this.editorHandlers().submit,
        },
      },
    });
  }

  /**
   * Perform thumbnail refresh checks for modified form activity. Keep checking for thumbnail changes
   * every 2 seconds. If the last modified date has updated, cancel the interval and refresh the activity.
   * 
   * Only take up to 10 attempts to refresh the activity.
   * 
   * @param activity The modified activity
   * @returns 
   */
  protected async onSaveCompletedForm(activity: Activity) {
    if (activity.kind !== 'Form') {
      return;
    }

    Enumerable
      .from(activity.attachments)
      .where(x => x.type === 'File')
      .doAction(x => {
        let lastThumbnailModified;
        const subscription =
          interval(this.INTERVAL_DURATION_IN_MS)
            .pipe(
              takeUntilDestroyed(this._destroyRef),
              startWith(0),
              take(this.MAX_REFRESH_ATTEMPTS),
              tap(() => {
                this.activityService
                  .fetchThumbnailHeader(x)
                  .pipe(
                    takeUntilDestroyed(this._destroyRef),
                    tap(lastModified => {

                      if (!lastModified) {
                        return;
                      }

                      if (!lastThumbnailModified) {
                        lastThumbnailModified = lastModified;
                      }

                      if (lastModified > lastThumbnailModified) {
                        lastThumbnailModified = null;
                        subscription.unsubscribe();
                        this.activityService.refreshActivity(activity._id);
                      }
                    })
                  )
                  .subscribe();
              })
            )
            .subscribe();
      })
      .firstOrDefault();
  }

  protected async onDelete(activityId: string) {

    const confirm = await this._confirmService.confirm('Delete Activity', 'Are you sure you wish to delete this Activity?', 'danger');

    if (!confirm)
      return;

    this.activityService.delete(activityId);
  }

  protected onShare(share: ShareableEntity) {
    this.activityService.share(share, false);
  }

  public refresh() {
    this.activityService.getAll(this.vm.context?.entity?._id);
    this._selectionService.clear();
    this.onFilter(this.vm.filter);
  }
}
